import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { BASE_URL } from "../../config/config";

// Get system settings (commission rates, etc.)
export const GetSystemSettings = createAsyncThunk(
  "settings/getSettings",
  async (_, { rejectWithValue }) => {
    try {
      const token = sessionStorage.getItem("ots_token") || localStorage.getItem("ots_token");
      const response = await axios.get(`${BASE_URL}/admin/settings/commission-rates`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: "Failed to fetch settings" });
    }
  }
);

// Update commission settings (Super Admin only)
export const UpdateCommissionSettings = createAsyncThunk(
  "settings/updateCommission",
  async ({ commissionData }, { rejectWithValue }) => {
    try {
      const token = sessionStorage.getItem("ots_token") || localStorage.getItem("ots_token");
      const response = await axios.put(
        `${BASE_URL}/admin/settings/commission`,
        commissionData,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: "Failed to update settings" });
    }
  }
);

// Get public settings (no auth required - for tenants/landlords)
export const GetPublicSettings = createAsyncThunk(
  "settings/getPublicSettings",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${BASE_URL}/settings/public`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: "Failed to fetch settings" });
    }
  }
);

const SystemSettingsSlice = createSlice({
  name: "systemSettings",
  initialState: {
    loading: false,
    settings: {
      // Default values (will be overwritten by API)
      // Set to 0 initially so that if someone bypasses loading check, they don't see hardcoded values
      advanceCommission: 0, // Will be set by API
      rentCommission: 0, // Will be set by API
      lateFee: 0, // Will be set by API
      gracePeriod: 5, // 5 days
      commissionPaidBy: "tenant", // 'tenant' or 'landlord' or 'split'
      isServiceActive: true,
      // The late-fee policy every new property is created with — admin's
      // call now, not the owner's. See PropertyRegistrationForm(New) for the
      // read-only display and CommissionSettings for where the admin sets it.
      latePaymentPolicy: {
        type: "PER_DAY_PERCENTAGE",
        amount: 0.5,
        maximumPercentage: 10,
        gracePeriodDays: 5,
      },
    },
    error: "",
    success: false,
    lastUpdated: null,
    settingsFetched: false, // Flag to indicate if settings have been fetched from API
  },
  reducers: {
    resetSettingsState: (state) => {
      state.error = "";
      state.success = false;
    },
  },
  extraReducers: (builder) => {
    // Get System Settings
    builder.addCase(GetSystemSettings.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(GetSystemSettings.fulfilled, (state, action) => {
      state.loading = false;
      if (action.payload?.data) {
        state.settings = { ...state.settings, ...action.payload.data };
      }
      state.lastUpdated = new Date().toISOString();
      state.settingsFetched = true;
    });
    builder.addCase(GetSystemSettings.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload?.message || "Failed to fetch settings";
      state.settingsFetched = true; // Mark as fetched even on error to allow component to proceed
    });

    // Update Commission Settings
    builder.addCase(UpdateCommissionSettings.pending, (state) => {
      state.loading = true;
      state.error = "";
      state.success = false;
    });
    builder.addCase(UpdateCommissionSettings.fulfilled, (state, action) => {
      state.loading = false;
      state.success = true;
      if (action.payload?.data) {
        state.settings = { ...state.settings, ...action.payload.data };
      }
      state.lastUpdated = new Date().toISOString();
    });
    builder.addCase(UpdateCommissionSettings.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload?.message || "Failed to update settings";
    });

    // Get Public Settings
    builder.addCase(GetPublicSettings.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(GetPublicSettings.fulfilled, (state, action) => {
      state.loading = false;
      if (action.payload?.data) {
        state.settings = { ...state.settings, ...action.payload.data };
      }
      state.settingsFetched = true;
    });
    builder.addCase(GetPublicSettings.rejected, (state, action) => {
      state.loading = false;
      state.settingsFetched = true; // Mark as fetched even on error to allow component to proceed
      // Don't show error for public settings - use defaults
    });
  },
});

export const { resetSettingsState } = SystemSettingsSlice.actions;
export default SystemSettingsSlice.reducer;
